if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.defaultSubmitButtonText = this.submitButtonText?.textContent;

        this.stickySubmitButton = this.querySelector('.sticky-atc-wrapper [type="submit"]');
        if (this.stickySubmitButton) {
          this.stickySubmitButtonText = this.stickySubmitButton.querySelector('span');
          this.defaultStickySubmitButtonText = this.stickySubmitButtonText?.textContent;
        }

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
          if (this.stickySubmitButton) {
            this.stickySubmitButton.setAttribute('aria-haspopup', 'dialog');
          }
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Helper to set loading state on both submit buttons (reduces code duplication)
      setButtonLoading(isLoading) {
        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          if (this.stickySubmitButton) {
            this.stickySubmitButton.setAttribute('aria-disabled', true);
            this.stickySubmitButton.classList.add('loading');
          }
        } else {
          this.submitButton.classList.remove('loading');
          if (this.stickySubmitButton) {
            this.stickySubmitButton.classList.remove('loading');
          }
        }
      }

      // Helper to enable/disable both submit buttons (reduces code duplication)
      setButtonDisabled(isDisabled) {
        if (isDisabled) {
          this.submitButton.setAttribute('aria-disabled', true);
          if (this.stickySubmitButton) {
            this.stickySubmitButton.setAttribute('aria-disabled', true);
          }
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          if (this.stickySubmitButton) {
            this.stickySubmitButton.removeAttribute('aria-disabled');
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if(this.stickySubmitButton) {
          if (this.stickySubmitButton.getAttribute('aria-disabled') === 'true') return;
        }

        this.handleErrorMessage();
        this.setButtonLoading(true);
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        const selectedVariant = this.getCurrentVariant();
        if (selectedVariant?.id) {
          const variantId = String(selectedVariant.id);
          formData.set('id', variantId);
          this.variantIdInput.value = variantId;
          this.variantIdInput.setAttribute('value', variantId);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');

              if(this.stickySubmitButton) {
                const stickySoldOutMessage = this.stickySubmitButton.querySelector('.sold-out-message');
                if (stickySoldOutMessage) {
                  this.stickySubmitButton.setAttribute('aria-disabled', true);
                  this.stickySubmitButtonText.classList.add('hidden');
                  stickySoldOutMessage.classList.remove('hidden');
                }
              }

              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            // Error handling: Show user-friendly message and reset UI state
            console.error('Error adding to cart:', e);
            this.handleErrorMessage('There was an error adding this item to your cart. Please try again.');
            this.error = true;
            // Ensure button is re-enabled after network errors so users can retry
            this.setButtonDisabled(false);
          })
          .finally(() => {
            this.setButtonLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            // Always remove aria-disabled to prevent dead clicks
            this.submitButton.removeAttribute('aria-disabled');
            if (this.stickySubmitButton) {
              this.stickySubmitButton.removeAttribute('aria-disabled');
            }
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.classList.remove('has-size-option');
          if (text) this.submitButtonText.textContent = text;

          if (this.stickySubmitButton) {
            this.stickySubmitButton.setAttribute('disabled', 'disabled');
            this.stickySubmitButton.classList.remove('has-size-option');
            if (text) this.stickySubmitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('has-size-option');
          const enabledText = text || this.defaultSubmitButtonText || (window.variantStrings?.addToCart);
          this.submitButtonText.textContent = enabledText;
          if (text) this.defaultSubmitButtonText = text;

          if (this.stickySubmitButton) {
            this.stickySubmitButton.removeAttribute('disabled');
            this.stickySubmitButton.classList.remove('has-size-option');
            this.stickySubmitButtonText.textContent = text || this.defaultStickySubmitButtonText || (window.variantStrings?.addToCart);
            if (text) this.defaultStickySubmitButtonText = text;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getCurrentVariant() {
        try {
          const variantScript = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
          if (variantScript) {
            const parsed = JSON.parse(variantScript.textContent || '{}');
            if (parsed && typeof parsed === 'object' && parsed.id) {
              return parsed;
            }
          }
        } catch (error) {
          console.error('Failed to resolve current variant', error);
        }
        const variantIdFromUrl = new URLSearchParams(window.location.search).get('variant');
        if (variantIdFromUrl) {
          return { id: variantIdFromUrl };
        }
        return null;
      }
    }
  );
}
